import React from 'react';
import { Clock } from 'lucide-react';
import type { UserGame } from '../types';

interface TopPlayedGamesProps {
  games: UserGame[];
}

export function TopPlayedGames({ games }: TopPlayedGamesProps) {
  const formatPlaytime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    if (hours < 1) return 'Less than 1 hour';
    return `${hours.toLocaleString()} ${hours === 1 ? 'hour' : 'hours'}`;
  };

  const getRankColor = (index: number): string => {
    switch (index) {
      case 0:
        return 'bg-yellow-500 text-white';
      case 1:
        return 'bg-gray-400 text-white';
      case 2:
        return 'bg-amber-600 text-white';
      default:
        return 'bg-gray-200 text-gray-700';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-xl font-semibold mb-4">Top Played Games</h2>
      <div className="space-y-4">
        {games.map((game, index) => (
          <div
            key={game.appid}
            className="group relative flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className={`flex-shrink-0 w-6 h-6 rounded-full ${getRankColor(index)} flex items-center justify-center text-sm font-medium`}>
              {index + 1}
            </div>
            <img
              src={`https://cdn.cloudflare.steamstatic.com/steam/apps/${game.appid}/capsule_sm_120.jpg`}
              alt={game.name}
              className="w-12 h-12 rounded object-cover"
            />
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-gray-900 truncate group-hover:text-indigo-600 transition-colors">
                {game.name}
              </h3>
              <div className="flex items-center gap-1.5 text-sm text-gray-500">
                <Clock className="w-4 h-4" />
                <span>{formatPlaytime(game.playtime_forever)}</span>
              </div>
              {game.playtime_2weeks && (
                <div className="mt-1 text-xs text-indigo-600">
                  {formatPlaytime(game.playtime_2weeks)} in the past 2 weeks
                </div>
              )}
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gray-100 group-hover:bg-gray-200 transition-colors"></div>
          </div>
        ))}
      </div>
    </div>
  );
}